<template>
  <div class="staff-credits-topup-form">
    <franchise-select
      v-model="formData.franquia_id"
      elem-id="topup-franquia-id"
      data-testid="topup-franquia-id"
      :label="t('Franquia')"
    />
    <input-number
      v-model="formData.credits"
      elem-id="topup-credits"
      :label="t('Créditos')"
      data-testid="topup-credits"
      :maxFractionDigits="2"
      :min="0"
    />
    <input-text
      v-model="formData.note"
      elem-id="topup-note"
      :label="t('Nota (opcional)')"
      data-testid="topup-note"
    />
    <div v-if="errors.franquia_id" class="error-message" data-testid="topup-error-franquia">{{ errors.franquia_id }}</div>
    <div v-if="errors.credits" class="error-message" data-testid="topup-error-credits">{{ errors.credits }}</div>
  </div>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue';

import { useI18n } from 'vue-i18n';
import { emitValidationFailed } from '../../../composables/useFormValidation';
import { eventBus } from '../../../eventBus';
import { useCreditWallet, MICRO_PER_CREDIT } from '../../../composables/useCreditWallet';
import { log } from '../../../utils/DebugLogger';
import type { AiCredits_TopupInput } from '../../../contracts/AiCredits_TopupInput';

const props = defineProps<{
  franquiaId?: number | string | null;
}>();

const { t } = useI18n();
const { topup } = useCreditWallet();

const emit = defineEmits<{
  'submit-success': [];
}>();

const formData = reactive({
  franquia_id: (props.franquiaId ?? null) as number | string | null,
  credits: null as number | null,
  note: '',
});

watch(() => props.franquiaId, (val) => {
  if (val != null) {
    formData.franquia_id = val;
  }
}, { immediate: true });


const errors = reactive({
  franquia_id: '',
  credits: '',
});

function validateForm(): boolean {
  errors.franquia_id = '';
  errors.credits = '';

  let valid = true;

  if (!formData.franquia_id) {
    errors.franquia_id = t('Selecione uma franquia');
    valid = false;
  }

  if (!formData.credits || formData.credits <= 0) {
    errors.credits = t('Informe a quantidade de créditos');
    valid = false;
  }

  if (!valid) {
    emitValidationFailed('StaffCreditsTopupForm', { ...errors });
  }

  return valid;
}

async function handleSubmit(): Promise<void> {
  log('StaffCreditsTopupForm', 'handleSubmit', 'Validating form');

  if (!validateForm()) {
    return;
  }

  const body: AiCredits_TopupInput = {
    franquia_id: Number(formData.franquia_id),
    micro_credits: Math.round((formData.credits as number) * MICRO_PER_CREDIT),
    request_id: crypto.randomUUID(),
    ...(formData.note ? { note: formData.note } : {}),
  };

  log('StaffCreditsTopupForm', 'handleSubmit', 'Submitting topup', body);
  try {
    await topup(body);
  } catch (err) {
    // A recusa do backend chega em HTTP 200 (envelope success:false) e agora
    // rejeita no httpService (throwOnEnvelopeFailure). Sem este catch a
    // rejeição não teria dono: o modal ficaria aberto e mudo.
    log('StaffCreditsTopupForm', 'handleSubmit', 'Erro ao adicionar créditos', {
      message: (err as Error)?.message,
    });
    eventBus.emit('globalMessage.error', t('Erro ao adicionar créditos'));
    return;
  }

  // Reset form
  formData.franquia_id = null;
  formData.credits = null;
  formData.note = '';

  emit('submit-success');
}

defineExpose({
  handleSubmit,
  formData,
});
</script>

<style scoped>
.staff-credits-topup-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.error-message {
  color: var(--p-red-600, #dc2626);
  font-size: 0.875rem;
}
</style>
